// The timeline selects the END of a rolling 24-hour window (ms since the
// epoch), or null for live. It has three zoom levels, each with its own
// slider range and step:
//
//   all   — the whole archive, one step per UTC day (step k = day k's news)
//   month — the 30 days up to the anchor, one step per UTC day
//   day   — the 24 hours of the anchor's UTC day (or the last 24 hours when
//           live), continuous (minute resolution)
//
// The last step of any range that reaches the present is "Live".
export const MINUTE_MS = 60_000;
export const HOUR_MS = 3_600_000;
export const DAY_MS = 24 * HOUR_MS;
// First day with data. Articles before May 2026 were migrated into
// articles_v4 from older collections (bluedot/scripts/migrate_archive.py);
// that archive has gaps (roughly May-Jul 2024 and Apr 2025-Jan 2026) and no
// categories.
export const TIMELINE_START = Date.UTC(2023, 9, 11);
export const SCALES = ["all", "month", "day"];
const MONTH_DAYS = 30;

const floorTo = (ms, unit) => Math.floor(ms / unit) * unit;
const ceilTo = (ms, unit) => Math.ceil(ms / unit) * unit;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

// Day index k covers the UTC day starting TIMELINE_START + k days; selecting it
// means the window that ends at midnight after it.
const dayIndexOf = (endMs) => Math.ceil((endMs - TIMELINE_START) / DAY_MS) - 1;
const dayEnd = (k) => TIMELINE_START + (k + 1) * DAY_MS;

const formatUtcDay = (ms, opts) =>
  new Date(ms).toLocaleDateString(undefined, { timeZone: "UTC", ...opts });
const formatLocalTime = (ms) =>
  new Date(ms).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

// Slider model for one scale. `anchor` is the selection when the scale was
// entered (a window end, or null for live); it fixes the month/day ranges.
export function buildScale(scale, anchor, nowMs = Date.now()) {
  const today = dayIndexOf(floorTo(nowMs, DAY_MS) + DAY_MS); // index of the UTC day in progress

  if (scale === "day") {
    // Days that haven't finished yet (today) show the last 24 hours instead.
    // Positions are fractional hours (step: one minute).
    const liveNow = anchor == null || ceilTo(anchor, DAY_MS) > floorTo(nowMs, MINUTE_MS);
    const end = liveNow ? floorTo(nowMs, MINUTE_MS) : ceilTo(anchor, DAY_MS);
    const start = end - DAY_MS;
    const min = Math.max(0, (TIMELINE_START + DAY_MS - start) / HOUR_MS);
    const toTime = (p) => (liveNow && p >= 24 ? null : start + Math.round(p * 60) * MINUTE_MS);
    const marks = [0, 6, 12, 18, 24].filter((p) => p >= min).map((p) => ({
      value: p,
      label: liveNow && p === 24 ? "Live" : formatLocalTime(start + p * HOUR_MS),
    }));
    return {
      scale, start, min, max: 24, step: 1 / 60, keyStep: 1, marks, toTime,
      toPos: (t) => (t == null ? 24 : clamp((t - start) / HOUR_MS, min, 24)),
      label: (p) => {
        const t = toTime(p);
        return t == null ? "Live" : new Date(t).toLocaleString(undefined, {
          day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
        });
      },
    };
  }

  // "all" and "month" both step by day; month just narrows the range.
  const last = scale === "month" && anchor != null ? clamp(dayIndexOf(anchor), 0, today) : today;
  const first = scale === "month" ? Math.max(0, last - MONTH_DAYS) : 0;
  const toTime = (k) => (k >= today ? null : dayEnd(k));
  const label = (k) => (k >= today ? "Live" : formatUtcDay(dayEnd(k) - 1, { day: "numeric", month: "short", year: "numeric" }));

  let marks;
  if (scale === "all") {
    // Year marks for a long archive; month marks (with the year on January)
    // while it is too short to hold more than a year boundary or two.
    marks = [];
    const start = new Date(TIMELINE_START);
    const monthsPerMark = today > 2 * 365 ? 12 : 1;
    for (let i = 0; ; i += monthsPerMark) {
      const first = Date.UTC(start.getUTCFullYear(), (monthsPerMark === 12 ? 0 : start.getUTCMonth()) + i, 1);
      if (first >= nowMs) break;
      const value = dayIndexOf(first + DAY_MS);
      if (value < 0) continue;
      const isJanuary = new Date(first).getUTCMonth() === 0;
      marks.push({ value, label: isJanuary ? String(new Date(first).getUTCFullYear()) : formatUtcDay(first, { month: "short" }) });
    }
  } else {
    marks = [];
    for (let k = last; k >= first; k -= 7) {
      marks.push({ value: k, label: formatUtcDay(dayEnd(k) - 1, { day: "numeric", month: "short" }) });
    }
  }
  if (last === today) {
    marks = marks.filter((m) => m.value !== today);
    marks.push({ value: today, label: "Live" });
  }

  return {
    scale, min: first, max: last, step: 1, keyStep: 1, marks, toTime, label,
    toPos: (t) => (t == null ? today : clamp(dayIndexOf(t), first, last)),
  };
}

// URL form of a window end in UTC: "YYYY-MM-DDTHH", or "YYYY-MM-DDTHH:MM"
// when it isn't on the hour.
export const formatTimeParam = (ms) => new Date(ms).toISOString().slice(0, ms % HOUR_MS ? 16 : 13);

// Window end from the URL: ?t=YYYY-MM-DDTHH[:MM], or a legacy ?date=YYYY-MM-DD,
// which meant "that day's news" — the window ending at midnight after it.
// Returns null (live) when absent, malformed or outside the timeline.
export function parseTimeParams(params, nowMs = Date.now()) {
  const t = params.get("t") || "";
  const date = params.get("date") || "";
  let ms = null;
  if (/^\d{4}-\d{2}-\d{2}T\d{2}(:\d{2})?$/.test(t)) {
    ms = Date.parse(t.length === 13 ? `${t}:00:00Z` : `${t}:00Z`);
    // Reject impossible dates that Date would roll over (e.g. Feb 30).
    if (Number.isNaN(ms) || formatTimeParam(ms) !== t) return null;
  } else if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const day = Date.parse(`${date}T00:00:00Z`);
    if (Number.isNaN(day) || new Date(day).toISOString().slice(0, 10) !== date) return null;
    ms = day + DAY_MS;
  } else {
    return null;
  }
  if (ms < TIMELINE_START + DAY_MS || ms > nowMs) return null;
  return ms;
}

// Scale from the URL; otherwise "day" for an hour-precise selection (so it
// isn't snapped to a whole day) and "all" for everything else.
export function parseScaleParam(params, time) {
  const s = params.get("scale");
  if (SCALES.includes(s)) return s;
  return time != null && time % DAY_MS !== 0 ? "day" : "all";
}
